import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Avatar, roleColorFor } from './hr/workspace/Avatar';
import {
  setTokenProvider, getAdminUsers, createAdminUser, deleteAdminUser,
  getEmployees, createEmployeeForUser, setEmployeeProductAccess, performUserAction,
  getEmployeeProfile, setEmployeeProfile, getHqCatalog,
  type EmployeeRecord, type HqCatalog,
} from '../api/client';
import { useHqAccess } from './settings/hqAccess';
import { RolePermissionsMatrix } from './settings/RolePermissionsMatrix';
import { RolesPanel } from './settings/RolesPanel';
import { HqAccessCard } from './settings/HqAccessCard';
import {
  rowState, filterDirectoryRows, summarizeDirectory, describeCounts, incompleteNotice, describeLoadProgress, NO_LOGIN_LABEL, emptyFilteredMessage,
  toggleLabel, toggleTitle, INACTIVE_BADGE, LOGIN_DISABLED_BADGE,
} from './usersDirectoryModel';
import type { LoginListStatus } from './usersDirectoryModel';
import { loadAllLogins } from './usersDirectoryLoader';
import LifecyclePanel from './LifecyclePanel';
import type { LoginPage } from './usersDirectoryLoader';

const STATUS_LABELS: Record<string, string> = {
  CONFIRMED: 'Active',
  FORCE_CHANGE_PASSWORD: 'Awaiting first login',
  UNCONFIRMED: 'Not confirmed',
  RESET_REQUIRED: 'Password reset required',
};

// Status badges (2026-10-13 Owner request): keep the green "Active" badge, and show a yellow
// "Pending" badge for a login that has not completed first sign-in. Other states get a short
// label and a neutral/amber tone. The `title` keeps the longer meaning on hover.
const STATUS_BADGES: Record<string, { label: string; className: string; title: string }> = {
  CONFIRMED: { label: 'Active', className: 'bg-green-100 text-green-700', title: 'Signed in and active' },
  FORCE_CHANGE_PASSWORD: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800', title: 'Invited — awaiting first login' },
  UNCONFIRMED: { label: 'Not confirmed', className: 'bg-gray-100 text-gray-500', title: 'Account not confirmed' },
  RESET_REQUIRED: { label: 'Reset required', className: 'bg-amber-100 text-amber-800', title: 'Password reset required' },
};

const ROLES = [
  { id: 'owner', label: 'Owner', description: 'Full access + Owner Strategic' },
  { id: 'admin', label: 'Admin', description: 'All modules + document management' },
  { id: 'warranty', label: 'Warranty', description: 'Warranty + Sales/Pricing' },
  { id: 'designer', label: 'Designer/Sales', description: 'Sales, CAD, Warranty, PM' },
  { id: 'pm', label: 'Project Manager', description: 'PM, Warranty, Sales/Pricing' },
  { id: 'superintendent', label: 'Superintendent', description: 'PM, Warranty, Sales/Pricing (field)' },
  { id: 'accounting', label: 'Accounting', description: 'Limited access' },
];

interface UserRecord {
  username: string;
  email: string;
  name: string;
  status: string;
  groups: string[];
  created: string;
  lastModified: string;
  capabilities: string[];
  // Cognito Enabled flag from GET /admin/users (the server already returns it). Optional because
  // older responses may omit it. Display only: it never decides who is "inactive".
  enabled?: boolean;
}

// Owner correction (2026-09-25): format a real Created/Modified timestamp for the table. Empty/
// missing is shown as an em dash rather than an invalid-looking blank cell.
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Owner correction (2026-09-25): shortened to numeric M/D/YY (e.g. "1/15/26") so Created/
  // Modified fit on one line at the narrower column widths this table now uses.
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

// Phase B — one merged row per real person. UniqueHQ login facts (Cognito) and canonical
// Employee/product-access facts are two different backend authorities (see admin/index.ts);
// this merge happens ONLY for display. It never recomputes access — every access fact shown
// is relayed verbatim from the Employee authority, exactly like the Employee list already did.
interface MergedAccessRow {
  key: string;
  username: string | null;
  displayName: string;
  email: string;
  cognitoStatus: string | null;
  loginEnabled: boolean | null;
  groups: string[];
  created: string | null;
  lastModified: string | null;
  capabilities: string[];
  employee: EmployeeRecord | null;
}

function buildMergedRows(users: UserRecord[], employees: EmployeeRecord[]): MergedAccessRow[] {
  // An Employee's identities include entries from OTHER pools too (Pricing/PM) — only match on
  // a subject that is actually a username in THIS (UniqueHQ) user list, never on cross-product
  // subjects, so a Pricing/PM sub never accidentally links to the wrong UniqueHQ login.
  const usernameSet = new Set(users.map((u) => u.username));
  const employeeByUsername = new Map<string, EmployeeRecord>();
  for (const emp of employees) {
    for (const id of emp.identities) {
      if (usernameSet.has(id.providerSubject) && !employeeByUsername.has(id.providerSubject)) {
        employeeByUsername.set(id.providerSubject, emp);
      }
    }
  }

  const rows: MergedAccessRow[] = [];
  const seenEmployeeIds = new Set<string>();
  for (const u of users) {
    const emp = employeeByUsername.get(u.username) || null;
    if (emp) seenEmployeeIds.add(emp.employeeId);
    rows.push({
      key: u.username,
      username: u.username,
      displayName: u.name || u.email,
      email: u.email,
      cognitoStatus: u.status,
      loginEnabled: typeof u.enabled === 'boolean' ? u.enabled : null,
      groups: u.groups,
      created: u.created,
      lastModified: u.lastModified,
      capabilities: u.capabilities || [],
      employee: emp,
    });
  }
  // An Employee with no matching login in THIS list (identity recorded under a pool this list
  // doesn't cover, or the login was since removed) still needs to be visible and manageable —
  // never silently dropped from the table.
  for (const emp of employees) {
    if (seenEmployeeIds.has(emp.employeeId)) continue;
    rows.push({
      key: emp.employeeId,
      username: null,
      displayName: 'Unnamed employee record',
      email: '',
      cognitoStatus: null,
      loginEnabled: null,
      groups: [],
      created: emp.createdAt,
      lastModified: null,
      capabilities: [],
      employee: emp,
    });
  }
  return rows;
}

// Per-row expand panel: employee registration, per-app access (give / remove), name editing, and
// password help. A pure presentation component driven entirely by props: it never fetches or
// recomputes access itself, matching the relay discipline the rest of this screen follows.
//
// 2026-10-13 usability cleanup (Owner feedback after the Jill Griffin walkthrough): compact three-
// column layout, plain-language wording, and no persistent technical explanations. Behavior is
// unchanged: every control below calls exactly the same handler with exactly the same arguments
// as before. The technical permission keys are still available, but behind a closed "Technical
// details" toggle instead of always-visible blue chips.
const APP_LABELS: Record<'uniquehq' | 'pricing' | 'pm', string> = {
  uniquehq: 'Unique HQ',
  pricing: 'Pricing',
  pm: 'Project Manager',
};

function ExpandedAccessPanel({
  row, profile, editingName, setEditingName, onSaveName,
  rowActionStatus, onMakeEmployee, onUserAction, onToggleProductAccess,
  isProtected, loginListComplete, onDeleteUser, onLifecycleChanged, nameOf, viewerIsPrimaryOwner, onAccessChanged,
}: {
  row: MergedAccessRow;
  profile: { firstName: string; lastName: string; source: string } | undefined;
  editingName: { employeeId: string; firstName: string; lastName: string } | null;
  setEditingName: (v: { employeeId: string; firstName: string; lastName: string } | null) => void;
  onSaveName: (employeeId: string) => void;
  rowActionStatus: Record<string, string>;
  onMakeEmployee: (username: string) => void;
  onUserAction: (username: string, action: 'require-password-change' | 'resend-invite' | 'reset-password') => void;
  onToggleProductAccess: (employeeId: string, product: 'pricing' | 'pm' | 'uniquehq', granted: boolean) => void;
  isProtected: boolean;
  loginListComplete: boolean;
  onDeleteUser: (username: string, label: string) => void;
  onLifecycleChanged: () => void;
  nameOf: (employeeId: string) => string;
  viewerIsPrimaryOwner: boolean;
  onAccessChanged: () => void;
}) {
  void onAccessChanged;
  const emp = row.employee;
  const isEditingThisRow = emp && editingName?.employeeId === emp.employeeId;
  const shownName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || row.displayName : row.displayName;

  return (
    // 2026-10-13 (Owner request): three equal white cards (33% each) matching the platform box
    // style — Employee · App access · Account.
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
      {/* Card 1: who they are, and whether they are registered as an employee */}
      <div className="bg-white rounded-lg border p-4">
        <section aria-label="Employee">
          <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Employee</h5>
          {emp ? (
            isEditingThisRow ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  aria-label="First name"
                  value={editingName!.firstName}
                  onChange={(e) => setEditingName({ ...editingName!, firstName: e.target.value })}
                  placeholder="First name"
                  className="px-2 py-1 border rounded text-sm w-28"
                />
                <input
                  type="text"
                  aria-label="Last name"
                  value={editingName!.lastName}
                  onChange={(e) => setEditingName({ ...editingName!, lastName: e.target.value })}
                  placeholder="Last name"
                  className="px-2 py-1 border rounded text-sm w-28"
                />
                <button onClick={() => onSaveName(emp.employeeId)} className="px-3 py-1 bg-blue text-white rounded text-xs font-medium">Save</button>
                <button onClick={() => setEditingName(null)} className="px-3 py-1 border rounded text-xs">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <span>{shownName}</span>
                <button
                  onClick={() => setEditingName({ employeeId: emp.employeeId, firstName: profile?.firstName || '', lastName: profile?.lastName || '' })}
                  className="text-xs text-blue hover:underline"
                >
                  Edit name
                </button>
              </div>
            )
          ) : (
            <span className="text-gray-700">{shownName}</span>
          )}
          {emp && rowActionStatus[`${emp.employeeId}:profile`] && (
            <p role="status" className="text-xs text-gray-700 mt-1">{rowActionStatus[`${emp.employeeId}:profile`]}</p>
          )}

          <div className="mt-2">
            {emp ? (
              <p className="text-xs text-green-700">Registered as an employee.</p>
            ) : row.username ? (
              <div>
                <p className="text-xs text-gray-600 mb-1.5">
                  Not registered as an employee yet. Register them to be able to give access to Pricing and Project Manager.
                </p>
                <button onClick={() => onMakeEmployee(row.username!)} className="px-3 py-1 bg-blue text-white rounded text-xs font-medium">
                  Register as employee
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                {loginListComplete
                  ? 'This employee record has no Unique HQ login.'
                  : 'No Unique HQ login was found in the part of the login list that loaded. The list is incomplete, so one may still exist.'}
              </p>
            )}
          </div>

          {/* Modified date moved here (2026-10-13): the table shows Created + Last Login only. */}
          {(row.created || row.lastModified) && (
            <p className="text-xs text-gray-500 mt-2">
              Created {formatDate(row.created)}{row.lastModified ? ` · Last modified ${formatDate(row.lastModified)}` : ''}
            </p>
          )}

          {/* Remove the Unique HQ login — moved into the panel (2026-10-13) so the row itself is
              just an expandable summary. A protected account (the Primary Platform Owner) shows a
              disabled note instead, matching what the server actually enforces. */}
          {row.username && !isProtected && (
            <button
              onClick={() => onDeleteUser(row.username!, row.email || row.displayName)}
              className="inline-block mt-3 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-red-600 hover:bg-red-50"
              title="Deletes only their Unique HQ sign-in. Their Employee record and any Pricing or Project Manager logins are left as they are."
            >
              Remove Unique HQ login
            </button>
          )}
          {row.username && isProtected && (
            <p className="text-xs text-gray-400 mt-2" title="Protected account — cannot be removed from this screen.">
              Protected account — cannot be removed here.
            </p>
          )}

          {/* BUILD-039: Deactivate / Reactivate. Offered only for a registered employee; the server decides who may act. */}
          {emp && (
            <LifecyclePanel employeeId={emp.employeeId} name={shownName} globalStatus={emp.globalStatus} onChanged={onLifecycleChanged} />
          )}
        </section>
      </div>

      {/* Card 2: which apps they can use */}
      <section aria-label="App access" className="bg-white rounded-lg border p-4">
        <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">App access</h5>
        {!emp ? (
          <p className="text-xs text-gray-500">Register this person as an employee to manage their app access.</p>
        ) : emp.globalStatus === 'disabled' ? (
          // BUILD-039: an inactive person cannot be granted access (the server refuses it), and a deactivation keeps their
          // app-access settings as they were. Show that plainly instead of Give/Remove buttons that would do nothing useful.
          <p className="text-xs text-gray-600" data-testid="inactive-app-access-note">
            This person is inactive, so none of the apps can be used. Their app-access settings are kept as they were. Reactivate them to change app access.
          </p>
        ) : (
          <div className="space-y-2">
            {([
              { product: 'uniquehq', emblem: 'HQ' },
              { product: 'pricing', emblem: 'PR' },
              { product: 'pm', emblem: 'PM' },
            ] as const).map(({ product, emblem }) => {
              const decision = emp.decisions[product];
              const key = `${emp.employeeId}:${product}`;
              const label = APP_LABELS[product];
              return (
                <div key={product}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        title={`${label}: ${decision.allowed ? 'has access' : 'no access'}`}
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold ring-1 flex-shrink-0 ${
                          decision.allowed ? 'bg-green-100 text-green-700 ring-green-300' : 'bg-gray-50 text-gray-300 ring-gray-200'
                        }`}
                      >
                        {emblem}
                      </span>
                      <span className="text-gray-800 truncate">{label}</span>
                    </span>
                    {decision.allowed ? (
                      <button
                        onClick={() => onToggleProductAccess(emp.employeeId, product, false)}
                        className="px-2 py-0.5 text-xs font-medium rounded-md border border-gray-300 text-red-600 hover:bg-red-50 whitespace-nowrap flex-shrink-0"
                        title={`Marks ${label} access as removed. It does not sign them out of a session that is already open.`}
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={() => onToggleProductAccess(emp.employeeId, product, true)}
                        className="px-2 py-0.5 text-xs font-medium rounded-md border border-gray-300 text-blue hover:bg-gray-100 whitespace-nowrap flex-shrink-0"
                        title={product === 'uniquehq' ? 'Turns Unique HQ access back on.' : `Sets up a ${label} sign-in for this person and emails them an invitation.`}
                      >
                        Give
                      </button>
                    )}
                  </div>
                  {rowActionStatus[key] && <p role="status" className="text-xs text-gray-700 mt-0.5">{rowActionStatus[key]}</p>}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Card 3: account — Unique HQ password actions plus company-level standing. */}
      <div className="bg-white rounded-lg border p-4">
        <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Account</h5>
        <div className="space-y-4">
        {row.username && (
          <section aria-label="Unique HQ password">
            <h6 className="text-xs font-medium text-gray-500 mb-1">Unique HQ password</h6>
            {/* 2026-10-13 (Owner request): short buttons instead of text links, and the longer
                "what this does" explanation moved off-screen into each button's hover tooltip
                rather than always-visible helper lines. */}
            {row.cognitoStatus === 'FORCE_CHANGE_PASSWORD' ? (
              <button
                onClick={() => onUserAction(row.username!, 'resend-invite')}
                className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                title="They have not signed in yet. Emails them a new invitation with a new temporary password."
              >
                Resend invite
              </button>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onUserAction(row.username!, 'reset-password')}
                  className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                  title="Emails them a reset code. Their current password keeps working until they use the code."
                >
                  Reset code
                </button>
                <button
                  onClick={() => onUserAction(row.username!, 'require-password-change')}
                  className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                  title="Their current password stops working now, and they are emailed a code to choose a new one."
                >
                  Force reset
                </button>
              </div>
            )}
          </section>
        )}

        <section aria-label="Company level">
          <h6 className="text-xs font-medium text-gray-500 mb-1">Company level</h6>
          {emp ? (
            <>
              <p className="text-xs text-gray-700">
                Company Admin: {emp.companyAdmin ? 'Yes' : 'No'} · Platform Owner: {emp.platformOwner.isOwner ? (emp.platformOwner.isPrimary ? 'Primary' : 'Yes') : 'No'}
              </p>
              {/* BUILD-039: shown, never ticked. Company-level authority is not a role permission. */}
              <p className="text-[11px] text-gray-500 mt-0.5" data-testid="company-level-protected">
                Company-level authority. It is not a permission that can be ticked here and is changed only through the protected account process.
              </p>
            </>
          ) : (
            <p className="text-xs text-gray-500">Applies once they are registered as an employee.</p>
          )}
          {/* The raw role permission-key list ("Technical details") was removed (2026-10-13 Owner
              request): it was read-only, meaningless to the operator, and duplicated what the
              Role column already conveys in plain terms. The backend role definition remains the
              source for those keys if a developer ever needs them. */}
        </section>

        {/* BUILD-039: Unique HQ role, individual permissions, Vendors and HR, in one place. */}
        {emp && (
          <div className="pt-3 border-t">
            <h6 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Access</h6>
            <HqAccessCard employeeId={emp.employeeId} name={shownName} active={emp.globalStatus !== 'disabled'} nameOf={nameOf} viewerIsPrimaryOwner={viewerIsPrimaryOwner} />
          </div>
        )}
        </div>
      </div>
      {row.username && rowActionStatus[row.username] && (
        <p role="status" className="md:col-span-3 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1">
          {rowActionStatus[row.username]}
        </p>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { user, getToken } = useAuth();
  // BUILD-039: User Management follows the server-resolved "Manage users" permission (or Company Admin / Platform Owner standing); the
  // server checks it again on every request.
  const hq = useHqAccess();
  const userIsAdmin = hq.can('hq.users.manage') || hq.canManageAccess;
  const [hqCatalog, setHqCatalog] = useState<HqCatalog | null>(null);
  async function loadHqCatalog() {
    const r = await getHqCatalog().catch(() => null);
    setHqCatalog(r && r.ok ? r.body : null);
  }
  useEffect(() => { if (hq.canManageAccess) loadHqCatalog(); }, [hq.canManageAccess]);
  function assignedRoleLabel(employeeId: string): string | null {
    if (!hqCatalog) return null;
    const a = hqCatalog.assignments.find((x) => x.employeeId === employeeId);
    return a ? (hqCatalog.roles.find((r) => r.roleId === a.roleId)?.label || null) : null;
  }
  // Initial tab (read once on mount; it behaves as a normal in-page tab afterwards):
  //  1. An explicit ?tab= in the URL always wins. PM's own Access screen links here with
  //     ?tab=users (Owner request 2026-09-25) so "Add PM user" lands directly on the table.
  //     (Vendors is no longer a Settings tab: /settings?tab=vendors is redirected to /vendors in App.tsx, BUILD-043.)
  //  2. Otherwise an admin defaults to User Management (where an admin usually wants to be),
  //     and everyone else defaults to My Profile (2026-10-13 Owner request).
  const tabParam = new URLSearchParams(window.location.search).get('tab');
  const initialTab: 'users' | 'profile' | 'system' | 'roadmap' | 'feedback' =
    tabParam === 'users' ? 'users'
    : tabParam === 'profile' ? 'profile'
    : userIsAdmin ? 'users'
    : 'profile';
  const [activeTab, setActiveTab] = useState<'users' | 'profile' | 'system' | 'roadmap' | 'feedback'>(initialTab);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [usersError, setUsersError] = useState('');
  // Pagination state (BUILD-039): whether every page of logins was read, progress while reading.
  const [loginList, setLoginList] = useState<LoginListStatus>({ complete: true });
  const [loadProgress, setLoadProgress] = useState<{ loaded: number; pages: number }>({ loaded: 0, pages: 0 });
  const usersLoadSeq = useRef(0);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [employeesError, setEmployeesError] = useState('');
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [rowActionStatus, setRowActionStatus] = useState<Record<string, string>>({});
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [profileNames, setProfileNames] = useState<Record<string, { firstName: string; lastName: string; source: string }>>({});
  const [editingName, setEditingName] = useState<{ employeeId: string; firstName: string; lastName: string } | null>(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  // User Management is organised into tabs inside the box: Directory (the user table, the main
  // tab) and Role Permissions (the reference). The chosen tab is remembered across visits
  // (2026-10-13 Owner request); defaults to Directory if storage is unavailable.
  // BUILD-039: three views — Directory (people), Roles (the role list and its lifecycle), Permissions (one role's default permissions).
  // BUILD-043: the "Vendors access" inventory tab was removed from User Management (the person-level
  // Vendors controls remain in each person's drawer, via VendorSourcesPanel). A persisted 'vendors'
  // preference from before this change safely falls back to Directory.
  const [userMgmtTab, setUserMgmtTab] = useState<'directory' | 'roles' | 'permissions'>(() => {
    try { const v = localStorage.getItem('settings.userMgmtTab'); return v === 'roles' || v === 'permissions' ? v : 'directory'; } catch { return 'directory'; }
  });
  const selectUserMgmtTab = (tab: 'directory' | 'roles' | 'permissions') => {
    setUserMgmtTab(tab);
    try { localStorage.setItem('settings.userMgmtTab', tab); } catch { /* ignore */ }
  };
  const [newUser, setNewUser] = useState({ email: '', firstName: '', lastName: '', role: 'pm' });
  const [createStatus, setCreateStatus] = useState('');
  const [roadmapItems, setRoadmapItems] = useState<any[]>([]);
  const [roadmapLoading, setRoadmapLoading] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ title: '', description: '', category: 'general' });
  const [showAddRoadmap, setShowAddRoadmap] = useState(false);
  const [newRoadmapItem, setNewRoadmapItem] = useState({ title: '', description: '', category: 'feature', priority: 'medium' });
  const [visibleStatuses, setVisibleStatuses] = useState<Set<string>>(new Set(['new', 'idea', 'planned', 'in-progress', 'submitted']));
  const [sortByDate, setSortByDate] = useState(false);

  const mergedRows = buildMergedRows(users, employees);
  // BUILD-039: names for previews and history ("who would be affected", "who changed this")
  const nameOfEmployee = (employeeId: string): string => {
    const r = mergedRows.find((x) => x.employee && x.employee.employeeId === employeeId);
    const p = profileNames[employeeId];
    return (p && `${p.firstName} ${p.lastName}`.trim()) || (r && r.displayName) || 'Someone not in this list';
  };

  // "Show inactive" (BUILD-039). Default OFF every time the screen opens: nothing is remembered, so
  // nobody is hidden by a stale preference. All rules live in usersDirectoryModel.ts (tested).
  // Inactive is ONLY employee.globalStatus === 'disabled'; pending invitations are never hidden.
  const [showInactive, setShowInactive] = useState(false);
  const employeesLoadedOk = !employeesLoading && !employeesError;
  const directorySummary = summarizeDirectory(mergedRows, showInactive, { loginList, employeesLoaded: employeesLoadedOk });
  const [pinnedRowKey, setPinnedRowKey] = useState<string | null>(null); // BUILD-039 lifecycle: see handleLifecycleChanged
  const baseVisibleRows = filterDirectoryRows(mergedRows, showInactive);
  const visibleRows = pinnedRowKey && expandedRow === pinnedRowKey
    ? mergedRows.filter((r) => r.key === pinnedRowKey || baseVisibleRows.includes(r))
    : baseVisibleRows;
  function handleToggleShowInactive(next: boolean) {
    setShowInactive(next);
    // Turning the filter off must not leave a now-hidden person's panel open.
    if (!next && expandedRow) {
      const open = mergedRows.find((r) => r.key === expandedRow);
      if (open && !filterDirectoryRows([open], false).length) setExpandedRow(null);
    }
  }

  useEffect(() => {
    setTokenProvider(getToken);
    loadUsers();
    loadEmployees();
    loadRoadmapItems();
  }, []);

  // UAT correction (2026-10-05, Owner-observed "Unnamed Employee" for the HR synthetic fixtures):
  // a canonical Employee with no matched Cognito login (e.g. an HR-only fixture, which by design
  // never has a login account) previously showed the hardcoded "Unnamed Employee (<id>…)"
  // placeholder until an admin manually expanded that row (ensureProfileLoaded only ran on
  // expand). The real name is already available via the SAME profile route HR's own directory
  // uses (platform_employee_profile, no Cognito needed) — this proactively resolves it for
  // every login-less Employee once the list loads, instead of waiting for a manual expand. This
  // is the same bounded-cohort N+1 tradeoff platform-employee-directory.ts already accepts for
  // Slice A's small DEV cohort; it is NOT a new identity source or a broader directory read.
  useEffect(() => {
    if (employees.length === 0) return;
    const usernameSet = new Set(users.map((u) => u.username));
    const loginless = employees.filter((emp) => !emp.identities.some((id) => usernameSet.has(id.providerSubject)));
    for (const emp of loginless) {
      ensureProfileLoaded(emp.employeeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, users]);

  // Reads EVERY page of HQ logins (BUILD-039 pagination). The loop, its bound, de-duplication and the
  // complete/incomplete answer live in usersDirectoryLoader.ts (tested). A newer load supersedes an
  // older one: the sequence number makes a stale load stop between pages and discard its result.
  async function loadUsers() {
    const seq = ++usersLoadSeq.current;
    setLoading(true);
    setUsersError('');
    setLoadProgress({ loaded: 0, pages: 0 });
    try {
      const result = await loadAllLogins<UserRecord>(
        (token) => getAdminUsers(token) as Promise<LoginPage<UserRecord>>,
        {
          onProgress: (loaded, pages) => { if (seq === usersLoadSeq.current) setLoadProgress({ loaded, pages }); },
          shouldAbort: () => seq !== usersLoadSeq.current,
        },
      );
      if (seq !== usersLoadSeq.current) return;
      setUsers(result.users);
      setLoginList(result.complete
        ? { complete: true }
        : { complete: false, reason: result.reason || 'continuation-failed', loaded: result.users.length, error: result.error });
    } catch (err: any) {
      if (seq !== usersLoadSeq.current) return;
      console.error('Failed to load users:', err);
      setUsersError(err?.message || 'Failed to load users');
    } finally {
      if (seq === usersLoadSeq.current) setLoading(false);
    }
  }

  async function loadEmployees(opts?: { quiet?: boolean }) {
    // `quiet` (BUILD-039 lifecycle): refresh the Employee facts WITHOUT swapping the table for the "Loading…" placeholder, so the
    // open Manage panel (and the result of the change just made) is not torn down mid-read.
    if (!opts?.quiet) setEmployeesLoading(true);
    setEmployeesError('');
    try {
      const data = await getEmployees();
      setEmployees(data.employees || []);
    } catch (err: any) {
      console.error('Failed to load employees:', err);
      setEmployeesError(err?.message || 'Failed to load canonical Employees');
    } finally {
      if (!opts?.quiet) setEmployeesLoading(false);
    }
  }

  // BUILD-039 lifecycle: after a Deactivate/Reactivate the person's row is kept visible while its panel stays open, even when
  // "Show inactive" is off, so the result (and any Retry) never disappears with the row. Closing the panel releases it.
  // Re-read the login list WITHOUT the "Loading…" placeholder, so the Status column follows the provider (a deactivation disables the
  // person's sign-ins). If it fails, the list already shown is kept as it was; nothing is invented.
  async function refreshLoginsQuietly() {
    const seq = ++usersLoadSeq.current;
    try {
      const result = await loadAllLogins<UserRecord>(
        (token) => getAdminUsers(token) as Promise<LoginPage<UserRecord>>,
        { shouldAbort: () => seq !== usersLoadSeq.current },
      );
      if (seq !== usersLoadSeq.current) return;
      setUsers(result.users);
      setLoginList(result.complete
        ? { complete: true }
        : { complete: false, reason: result.reason || 'continuation-failed', loaded: result.users.length, error: result.error });
    } catch (err) {
      console.error('Quiet login refresh failed; keeping the list already shown:', err);
    }
  }
  function handleLifecycleChanged(rowKey: string) {
    setPinnedRowKey(rowKey);
    void loadEmployees({ quiet: true });
    void refreshLoginsQuietly();
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    setCreateStatus('Creating login...');
    try {
      await createAdminUser(newUser);
      // Plain wording (2026-10-13): the server's own message names the identity provider; the
      // admin only needs to know that an invitation went out and what it contains.
      setCreateStatus(`Invitation emailed to ${newUser.email}. It contains a temporary password they replace the first time they sign in.`);
      setNewUser({ email: '', firstName: '', lastName: '', role: 'pm' });
      setShowCreateUser(false);
      loadUsers();
      setTimeout(() => setCreateStatus(''), 8000);
    } catch (err: any) {
      setCreateStatus(`Could not create the login: ${err.message}`);
    }
  }

  async function handleDeleteUser(username: string, label: string) {
    if (!confirm(`Remove the Unique HQ login for ${label}?\n\nThis deletes only their Unique HQ sign-in. Their Employee record and any separate Pricing or Project Manager logins are left as they are.`)) return;
    try {
      await deleteAdminUser(username);
      loadUsers();
    } catch (err: any) {
      alert(`Failed to remove user: ${err.message || 'unknown error'}`);
    }
  }

  async function handleMakeEmployee(username: string) {
    setRowActionStatus((s) => ({ ...s, [username]: 'Registering as an employee...' }));
    try {
      const result: any = await createEmployeeForUser(username);
      if (result?.code) {
        setRowActionStatus((s) => ({ ...s, [username]: `Could not register: ${result.message}` }));
        return;
      }
      setRowActionStatus((s) => ({ ...s, [username]: 'Registered as an employee.' }));
      await loadEmployees();
      setTimeout(() => setRowActionStatus((s) => ({ ...s, [username]: '' })), 4000);
    } catch (err: any) {
      setRowActionStatus((s) => ({ ...s, [username]: `Could not register: ${err.message}` }));
    }
  }

  async function handleUserAction(username: string, action: 'require-password-change' | 'resend-invite' | 'reset-password') {
    const pendingLabel = action === 'resend-invite' ? 'Resending invitation...'
      : action === 'reset-password' ? 'Emailing a reset code...'
      : 'Forcing a password reset...';
    setRowActionStatus((s) => ({ ...s, [username]: pendingLabel }));
    try {
      await performUserAction(username, action);
      // Plain wording (2026-10-13). Same server call and same success/failure handling as before;
      // only the text shown to the admin changed. Each message says what happened to this
      // person's Unique HQ password only.
      const doneLabel = action === 'resend-invite'
        ? 'A new invitation with a new temporary password was emailed. They have not signed in yet.'
        : action === 'reset-password'
        ? 'A reset code was emailed. Their current password keeps working until they use the code.'
        : 'Their Unique HQ password no longer works. A reset code was requested so they can choose a new one. Pricing and Project Manager logins are not affected.';
      setRowActionStatus((s) => ({ ...s, [username]: doneLabel }));
      if (action === 'require-password-change') {
        await loadUsers();
      }
      setTimeout(() => setRowActionStatus((s) => ({ ...s, [username]: '' })), 8000);
    } catch (err: any) {
      setRowActionStatus((s) => ({ ...s, [username]: `That did not work: ${err.message}` }));
    }
  }

  // Product access grant/revoke — relays the truthful pending/success/failure result from the
  // platform authority. Never recomputes access itself; never claims access is on before the
  // backend confirms provisioning actually succeeded (see admin/index.ts's own discipline).
  async function handleToggleProductAccess(employeeId: string, product: 'pricing' | 'pm' | 'uniquehq', granted: boolean) {
    const key = `${employeeId}:${product}`;
    setRowActionStatus((s) => ({ ...s, [key]: granted ? 'Giving access...' : 'Removing access...' }));
    try {
      const result = await setEmployeeProductAccess(employeeId, product, granted);
      if (result.provisioning && result.provisioning.outcome !== 'provisioned' && result.provisioning.outcome !== 'already-provisioned') {
        setRowActionStatus((s) => ({ ...s, [key]: `Could not set up the sign-in (${result.provisioning!.reason || result.provisioning!.outcome}). Access was not given. It is safe to try again.` }));
      } else if (result.provisioning) {
        // A Pricing or PM grant sets up that app's own sign-in and emails an invitation. Short,
        // plain confirmation (2026-10-13 Owner wording). The already-provisioned case is NOT a
        // fresh invitation, so it says so rather than claiming an email went out.
        setRowActionStatus((s) => ({
          ...s,
          [key]: result.provisioning!.outcome === 'already-provisioned'
            ? 'Access enabled. They already had a sign-in for this app.'
            : 'Access enabled. Invitation emailed.',
        }));
      } else {
        setRowActionStatus((s) => ({
          ...s,
          [key]: granted
            ? 'Access is on.'
            : 'Access removed on record. This does not sign them out of a session that is already open.',
        }));
      }
      await loadEmployees();
    } catch (err: any) {
      setRowActionStatus((s) => ({ ...s, [key]: `That did not work: ${err.message}. It is safe to try again.` }));
    }
  }

  async function ensureProfileLoaded(employeeId: string) {
    if (profileNames[employeeId]) return;
    try {
      const profile = await getEmployeeProfile(employeeId);
      setProfileNames((p) => ({
        ...p,
        [employeeId]: {
          firstName: profile.fields.first_name || '',
          lastName: profile.fields.last_name || '',
          source: profile.source.first_name || profile.source.last_name || 'none',
        },
      }));
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  }

  function toggleExpandedRow(key: string, employeeId: string | undefined) {
    setExpandedRow((cur) => (cur === key ? null : key));
    if (employeeId) ensureProfileLoaded(employeeId);
  }

  async function handleSaveName(employeeId: string) {
    if (!editingName) return;
    const key = `${employeeId}:profile`;
    setRowActionStatus((s) => ({ ...s, [key]: 'Saving...' }));
    try {
      await setEmployeeProfile(employeeId, { first_name: editingName.firstName, last_name: editingName.lastName });
      setProfileNames((p) => ({ ...p, [employeeId]: { firstName: editingName.firstName, lastName: editingName.lastName, source: 'canonical' } }));
      setEditingName(null);
      setRowActionStatus((s) => ({ ...s, [key]: 'Name updated.' }));
      setTimeout(() => setRowActionStatus((s) => ({ ...s, [key]: '' })), 4000);
    } catch (err: any) {
      setRowActionStatus((s) => ({ ...s, [key]: `Could not save the name: ${err.message}` }));
    }
  }

  async function loadRoadmapItems() {
    setRoadmapLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${getApiUrl()}/roadmap`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRoadmapItems(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load roadmap:', err);
    } finally {
      setRoadmapLoading(false);
    }
  }

  async function handleSubmitFeedback(e: React.FormEvent) {
    e.preventDefault();
    if (!feedbackForm.title.trim()) return;
    try {
      const token = await getToken();
      await fetch(`${getApiUrl()}/roadmap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...feedbackForm, type: 'feedback' }),
      });
      setFeedbackForm({ title: '', description: '', category: 'general' });
      loadRoadmapItems();
    } catch (err) {
      alert('Failed to submit feedback');
    }
  }

  async function handleUpdateStatus(itemId: string, status: string) {
    try {
      const token = await getToken();
      await fetch(`${getApiUrl()}/roadmap/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      loadRoadmapItems();
    } catch (err) {
      alert('Failed to update status');
    }
  }

  async function handleDeleteRoadmapItem(itemId: string) {
    if (!confirm('Delete this item?')) return;
    try {
      const token = await getToken();
      await fetch(`${getApiUrl()}/roadmap/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      loadRoadmapItems();
    } catch (err) {
      alert('Failed to delete item');
    }
  }

  async function handleAddNote(itemId: string, note: string) {
    try {
      const token = await getToken();
      await fetch(`${getApiUrl()}/roadmap/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ adminNotes: note }),
      });
      loadRoadmapItems();
    } catch (err) {
      alert('Failed to save note');
    }
  }

  async function handleAddRoadmapItem(e: React.FormEvent) {
    e.preventDefault();
    if (!newRoadmapItem.title.trim()) return;
    try {
      const token = await getToken();
      await fetch(`${getApiUrl()}/roadmap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...newRoadmapItem, type: 'idea', status: 'idea' }),
      });
      setNewRoadmapItem({ title: '', description: '', category: 'feature', priority: 'medium' });
      setShowAddRoadmap(false);
      loadRoadmapItems();
    } catch (err) {
      alert('Failed to add item');
    }
  }

  function toggleStatus(status: string) {
    setVisibleStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) { next.delete(status); } else { next.add(status); }
      return next;
    });
  }

  function getApiUrl() {
    return import.meta.env.VITE_API_URL || 'https://oa2v2yv978.execute-api.us-east-1.amazonaws.com';
  }

  // 2026-10-13 (Owner request): match the platform background HR uses (#eef1f6, the darker
  // blue-grey) so the white content boxes stand out, and present the sidebar + content as cards
  // floating on that background. The "← Back" header button was removed — Settings is a
  // top-level destination with nothing to go back to.
  return (
    <div className="h-screen flex flex-col bg-[#eef1f6]">
      {/* Header */}
      <div className="h-14 flex items-center px-6">
        <h3 className="font-semibold text-gray-700">Settings</h3>
      </div>

      <div className="flex-1 flex overflow-hidden gap-5 px-6 pb-6">
        {/* Settings sidebar — a white card on the platform background, like HR's nav. */}
        <div className="w-56 flex-shrink-0 bg-white rounded-lg border p-3 space-y-1 h-fit">
          {userIsAdmin && (
            <button
              onClick={() => setActiveTab('users')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${activeTab === 'users' ? 'bg-blue/10 text-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              👥 User Management
            </button>
          )}
          <button
            onClick={() => setActiveTab('profile')}
            className={`w-full text-left px-3 py-2 rounded text-sm ${activeTab === 'profile' ? 'bg-blue/10 text-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            👤 My Profile
          </button>
          {userIsAdmin && (
            <button
              onClick={() => setActiveTab('system')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${activeTab === 'system' ? 'bg-blue/10 text-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              ⚙️ System
            </button>
          )}
          {userIsAdmin && (
            <button
              onClick={() => setActiveTab('roadmap')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${activeTab === 'roadmap' ? 'bg-blue/10 text-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              🗺️ Roadmap
            </button>
          )}
          <button
            onClick={() => setActiveTab('feedback')}
            className={`w-full text-left px-3 py-2 rounded text-sm ${activeTab === 'feedback' ? 'bg-blue/10 text-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            💬 Feedback
          </button>
        </div>

        {/* Content area. The Users tab needs the FULL available width (Name/Email/Role/Status/
            Created/Modified/Product Access/Actions) — capping it at max-w-4xl clipped the
            Actions column while space sat unused to the right (Owner correction, 2026-09-25).
            The table wrapper below still provides its own horizontal scroll as a fallback for a
            genuinely narrow window, rather than ever silently clipping controls. Every other tab
            keeps the narrower reading-width cap unchanged. */}
        <div className={`flex-1 overflow-y-auto min-w-0 ${activeTab === 'users' ? 'w-full' : 'max-w-4xl'}`}>
          {activeTab === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-navy">User Management</h2>
                  <p className="text-sm text-gray-500">Manage who can sign in to Unique HQ and which apps they can use.</p>
                </div>
                {/* Top right, beside Add User (Owner request, BUILD-039). Default OFF. A real checkbox
                    inside a label so the whole label is clickable and screen readers announce the
                    state. Disabled only when inactive status cannot be known (employee list failed). */}
                <div className="flex items-center gap-4">
                  <label
                    className={`flex items-center gap-2 text-sm select-none ${directorySummary.inactiveStatusKnown ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
                    title={toggleTitle(directorySummary)}
                  >
                    <input
                      type="checkbox"
                      data-testid="show-inactive-toggle"
                      checked={showInactive}
                      disabled={!directorySummary.inactiveStatusKnown}
                      onChange={(e) => handleToggleShowInactive(e.target.checked)}
                      className="rounded border-gray-300 focus:ring-2 focus:ring-blue"
                    />
                    <span data-testid="show-inactive-label">{toggleLabel(directorySummary)}</span>
                  </label>
                  <button
                    onClick={() => setShowCreateUser(!showCreateUser)}
                    className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-medium hover:bg-opacity-90"
                  >
                    + Add User
                  </button>
                </div>
              </div>

              {createStatus && (
                <div className={`mb-4 p-3 rounded text-sm ${createStatus.startsWith('Could not') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  {createStatus}
                </div>
              )}

              {/* Create user form */}
              {showCreateUser && (
                <div className="mb-6 p-4 bg-white rounded-lg border">
                  <h4 className="font-medium mb-3">Add New User</h4>
                  <form onSubmit={handleCreateUser} className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                      <input
                        type="text"
                        value={newUser.firstName}
                        onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                        className="w-full px-3 py-2 border rounded text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                      <input
                        type="text"
                        value={newUser.lastName}
                        onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                        className="w-full px-3 py-2 border rounded text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="w-full px-3 py-2 border rounded text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        className="w-full px-3 py-2 border rounded text-sm"
                      >
                        {/* BUILD-039 R1: Owner and Admin logins are created only by a Platform Owner (the server enforces it too). */}
                        {ROLES.filter((r) => hq.platformOwner || !['owner', 'admin'].includes(r.id)).map(r => (
                          <option key={r.id} value={r.id}>{r.label} — {r.description}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-gray-500 mt-1">This is their starting Unique HQ role. A custom role can be given afterwards from their Access panel.</p>
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <button type="submit" className="px-4 py-2 bg-blue text-white rounded text-sm font-medium">
                        Create User
                      </button>
                      <button type="button" onClick={() => setShowCreateUser(false)} className="px-4 py-2 border rounded text-sm">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* The cgriffin/pmadmin shared-identity "Heads up" banner was removed per Owner
                  request (2026-10-13): the Owner is aware of the shared PM identity and does not
                  want to be notified of it on this screen. No account or permission behavior is
                  affected by removing the banner. */}

              {/* Unified People & Access table (Phase B) — one row per real person, merging
                  UniqueHQ login facts with canonical Employee / product-access facts. Matches
                  Pricing's People & Access table pattern (Name/Email/Role/Status/Created/
                  Modified/Actions) with an added expand row for per-product Grant/Revoke and
                  Business Permissions, per the Owner's reference screenshot. */}
              {/* the Roles view fits the width itself; a horizontal scroll box here would also stop its role toolbar from staying in view */}
              <div className={`bg-white rounded-lg border ${userMgmtTab === 'directory' ? 'overflow-x-auto' : ''}`}>
                {/* Tabs at the top of the box: Directory (main) and Role Permissions. */}
                <div className="border-b" role="tablist" aria-label="User management views">
                  <div className="flex gap-1 px-2 pt-2">
                    {([
                      { id: 'directory', label: 'Directory' },
                      { id: 'roles', label: 'Roles' },
                            { id: 'permissions', label: 'Permissions' },
                    ] as const).map((t) => (
                      <button
                        key={t.id}
                        role="tab"
                        aria-selected={userMgmtTab === t.id}
                        onClick={() => selectUserMgmtTab(t.id)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-blue ${
                          userMgmtTab === t.id
                            ? 'border-blue text-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {userMgmtTab === 'directory' && (
                <div className="p-4 border-b">
                  <p className="text-xs text-gray-500">
                    Everyone with a Unique HQ login, and which apps they can use. Click a name to manage their
                    employee registration, app access, name, or password.
                  </p>
                  {/* Honest counts (BUILD-039): only once both lists have settled, never while loading
                      or after a login-list error, so a partial number is never presented as final. */}
                  {!loading && !employeesLoading && !usersError && (
                    <p className="text-xs text-gray-600 mt-2" data-testid="directory-counts" role="status" aria-live="polite">
                      {describeCounts(directorySummary)}
                    </p>
                  )}
                  {!loading && !usersError && incompleteNotice(directorySummary) && (
                    <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="directory-incomplete-notice" role="alert">
                      <span>{incompleteNotice(directorySummary)}</span>{' '}
                      <button onClick={loadUsers} className="underline hover:no-underline" data-testid="directory-incomplete-retry">Retry</button>
                    </div>
                  )}
                  {!loading && !employeesLoading && employeesError && (
                    <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="directory-status-unknown">
                      Inactive status could not be loaded, so every login is shown and none are labelled inactive.
                    </p>
                  )}
                </div>
                )}

                {/* BUILD-039: the real, editable Roles & Permissions matrix (was a static reference table). */}
                {userMgmtTab === 'roles' && <RolesPanel nameOf={nameOfEmployee} onEditPermissions={() => selectUserMgmtTab('permissions')} />}
                {userMgmtTab === 'permissions' && <RolePermissionsMatrix nameOf={nameOfEmployee} />}

                {userMgmtTab === 'directory' && ((loading || employeesLoading) ? (
                  <div className="p-4 text-gray-500" data-testid="directory-loading" role="status" aria-live="polite">
                    {loading ? describeLoadProgress(loadProgress.loaded, loadProgress.pages) : 'Loading…'}
                  </div>
                ) : usersError ? (
                  <div className="p-4 text-red-600">
                    <p>Could not load users: {usersError}</p>
                    <button onClick={loadUsers} className="text-xs mt-1 underline hover:no-underline">Retry</button>
                  </div>
                ) : mergedRows.length === 0 ? (
                  <div className="p-4 text-gray-500">
                    <p>No users found.</p>
                  </div>
                ) : visibleRows.length === 0 ? (
                  <div className="p-4 text-gray-500" data-testid="directory-all-hidden">
                    <p>{emptyFilteredMessage(directorySummary)}</p>
                  </div>
                ) : (
                  // Owner correction (2026-09-25): fixed the Actions column being clipped while
                  // space sat unused to the right. This wrapper is the ONLY horizontal-scroll
                  // boundary (a visible, native scrollbar appears only if the viewport is
                  // genuinely too narrow for the content — nothing is silently clipped, and no
                  // browser zoom-out is required). min-w-full plus per-column widths let the
                  // table itself use all available width on a normal desktop viewport instead of
                  // being squeezed by the page's own max-w cap. Long names/emails wrap instead of
                  // forcing extra horizontal scroll.
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <colgroup>
                        <col className="w-52" />
                        <col className="w-44" />
                        <col className="w-24" />
                        <col className="w-24" />
                        <col className="w-24" />
                        <col className="w-24" />
                        <col className="w-48" />
                      </colgroup>
                      <thead>
                        <tr className="text-left text-gray-500 border-b bg-gray-50">
                          <th className="px-3 py-2 font-medium">Name</th>
                          <th className="px-3 py-2 font-medium">Email</th>
                          <th className="px-3 py-2 font-medium whitespace-nowrap" title="The person's Unique HQ role">Role</th>
                          <th className="px-3 py-2 font-medium">Status</th>
                          <th className="px-3 py-2 font-medium">Created</th>
                          {/* "Modified" was removed from the table (2026-10-13 Owner request); it
                              now appears only in the expanded panel. The table shows Created and
                              Last Login. */}
                          {/* Last Login — placeholder column only (2026-09-25). NOT wired to real
                              data yet: a real value requires AdminListUserAuthEvents, which needs
                              Cognito
                              Advanced Security enabled on this pool (a paid feature we haven't
                              confirmed the cost/status of) and is a per-user API call, not a bulk
                              one like Created/Modified. Tracked as a deployment-list item. */}
                          <th className="px-3 py-2 font-medium">Last Login</th>
                          <th className="px-3 py-2 font-medium">App access</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {visibleRows.map((row) => {
                          const isExpanded = expandedRow === row.key;
                          const rs = rowState(row, loginList.complete);
                          const emp = row.employee;
                          // Owner correction (2026-09-25): matches the backend's own protection
                          // rule exactly (the Primary Platform Owner) rather than a separate,
                          // hardcoded UI list that could drift from what the server actually
                          // enforces. The server refuses this delete regardless of what the UI
                          // shows; this just makes that boundary visible before it's clicked.
                          const isProtected = !!emp?.platformOwner?.isPrimary;
                          return (
                            <React.Fragment key={row.key}>
                              {/* 2026-10-13: the entire row and the name open the details. The old
                                  tiny expand triangle was removed (Owner feedback: too small); a
                                  larger, labelled "Manage" control sits at the end of the row.
                                  Each inner control stops propagation so the row toggles once. */}
                              <tr
                                data-testid="directory-row"
                                data-row-key={row.key}
                                className={`cursor-pointer hover:bg-gray-50 align-top ${isExpanded ? 'bg-gray-50' : ''}`}
                                onClick={() => toggleExpandedRow(row.key, emp?.employeeId)}
                              >
                                <td className="px-3 py-3 font-medium whitespace-nowrap">
                                  {(() => {
                                    // Bug fix (2026-09-25): the main row was always showing the
                                    // static UniqueHQ-login name, never the edited canonical
                                    // profile name saved via "Edit name" in the expand panel below
                                    // — so an edit appeared to succeed there but never showed up
                                    // in the row you'd actually scan. Same precedence the expand
                                    // panel already uses: edited canonical name wins when present.
                                    const profile = emp ? profileNames[emp.employeeId] : undefined;
                                    const editedName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
                                    const shown = editedName || row.displayName;
                                    return (
                                      // Circular profile avatar before the name: initials now, a
                                      // real picture later once uploads exist (same Avatar used by
                                      // the HR roster, so the look stays consistent). Colour is
                                      // derived deterministically from the person's id/name.
                                      <div className="flex items-center gap-2.5">
                                        <Avatar name={shown} colorKey={emp?.employeeId || row.key} color={roleColorFor(row.groups)} sizeClass="w-8 h-8" textClass="text-xs" />
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); toggleExpandedRow(row.key, emp?.employeeId); }}
                                          aria-expanded={isExpanded}
                                          className="text-left text-base font-semibold text-gray-900 hover:text-blue hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue rounded"
                                        >
                                          {shown}
                                        </button>
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{row.email || '—'}</td>
                                <td className="px-3 py-3 text-gray-600" data-testid={`role-${row.key}`}>{(emp && assignedRoleLabel(emp.employeeId)) || row.groups.join(', ') || 'No role'}</td>
                                <td className="px-3 py-3" data-testid={`status-${row.key}`}>
                                  <div className="flex flex-wrap items-center gap-1">
                                  {row.cognitoStatus ? (() => {
                                    // A login that Cognito reports as disabled keeps UserStatus CONFIRMED, which would
                                    // otherwise read as a green "Signed in and active" badge. Say what is true instead.
                                    const badge = rs.loginDisabled && row.cognitoStatus === 'CONFIRMED' ? LOGIN_DISABLED_BADGE : STATUS_BADGES[row.cognitoStatus];
                                    return badge ? (
                                      <span title={badge.title} className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.className}`}>
                                        {badge.label}
                                      </span>
                                    ) : (
                                      <span className="text-gray-600 text-xs">{STATUS_LABELS[row.cognitoStatus] || row.cognitoStatus}</span>
                                    );
                                  })() : (
                                    rs.loginUnconfirmed ? (
                                      <span className="text-amber-700 text-xs" title={NO_LOGIN_LABEL.unconfirmed.title} data-testid="login-unconfirmed">{NO_LOGIN_LABEL.unconfirmed.label}</span>
                                    ) : (
                                      <span className="text-gray-400 text-xs" title={NO_LOGIN_LABEL.confirmed.title}>{NO_LOGIN_LABEL.confirmed.label}</span>
                                    )
                                  )}
                                  {rs.inactive && (
                                    <span
                                      data-testid="inactive-badge"
                                      title={INACTIVE_BADGE.title}
                                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${INACTIVE_BADGE.className}`}
                                    >
                                      {INACTIVE_BADGE.label}
                                    </span>
                                  )}
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-gray-500 text-xs">{formatDate(row.created)}</td>
                                {/* Last Login — deferred this release per the BUILD-039 bounded
                                    DEV release authorization (2026-09-25): no basic-tier Cognito
                                    field exposes last-successful-sign-in without either a paid
                                    Advanced Security upgrade or a new PostAuthentication trigger +
                                    persistence layer, both of which would materially expand this
                                    release's scope/risk. "Not yet tracked" per the authorization's
                                    explicit unknown-history wording — never "Never" or a
                                    fabricated date, and never implies ongoing session usage. */}
                                <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">Not yet tracked</td>
                                <td className="px-3 py-3">
                                  {emp ? (
                                    <div className="flex flex-wrap gap-1.5">
                                      {([
                                        { short: 'HQ', label: 'Unique HQ', on: emp.decisions.uniquehq.allowed },
                                        { short: 'PR', label: 'Pricing', on: emp.decisions.pricing.allowed },
                                        { short: 'PM', label: 'Project Manager', on: emp.decisions.pm.allowed },
                                      ] as const).map((a) => (
                                        <span
                                          key={a.short}
                                          title={`${a.label}: ${a.on ? 'has access' : 'no access'}`}
                                          className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-semibold ring-1 ${
                                            a.on
                                              ? 'bg-green-100 text-green-700 ring-green-300 shadow-sm'
                                              : 'bg-gray-50 text-gray-300 ring-gray-200'
                                          }`}
                                        >
                                          {a.short}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400" title="Not registered as an employee yet. Open the row to register them.">Not registered</span>
                                  )}
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan={7} className="px-4 py-4 bg-gray-50 border-b">
                                    <ExpandedAccessPanel
                                      row={row}
                                      profile={emp ? profileNames[emp.employeeId] : undefined}
                                      editingName={editingName}
                                      setEditingName={setEditingName}
                                      onSaveName={handleSaveName}
                                      rowActionStatus={rowActionStatus}
                                      onMakeEmployee={handleMakeEmployee}
                                      onUserAction={handleUserAction}
                                      onToggleProductAccess={handleToggleProductAccess}
                                      isProtected={isProtected}
                                      loginListComplete={loginList.complete}
                                      onDeleteUser={handleDeleteUser}
                                      onLifecycleChanged={() => handleLifecycleChanged(row.key)}
                                      nameOf={nameOfEmployee}
                                      viewerIsPrimaryOwner={hq.primaryOwner}
                                      onAccessChanged={loadHqCatalog}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
                {userMgmtTab === 'directory' && employeesError && (
                  <div className="p-3 text-xs text-red-600 border-t">
                    Could not load app access data: {employeesError}{' '}
                    <button onClick={() => loadEmployees()} className="underline hover:no-underline">Retry</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div>
              <h2 className="text-lg font-semibold text-navy mb-2">My Profile</h2>
              <p className="text-sm text-gray-500 mb-6">Personal settings and preferences for your AI interactions.</p>

              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium mb-3">Email Signature</h4>
                  <p className="text-xs text-gray-500 mb-2">Used when the AI drafts emails on your behalf.</p>
                  <textarea
                    className="w-full px-3 py-2 border rounded text-sm"
                    rows={4}
                    placeholder={"Chris Griffin\nOwner / Sales Manager\nUnique Companies\n480-969-1911"}
                    disabled
                  />
                  <p className="text-xs text-gray-400 mt-1">Coming soon</p>
                </div>

                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium mb-3">Communication Preferences</h4>
                  <p className="text-xs text-gray-500 mb-2">How you want the AI to communicate.</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" disabled checked className="rounded" />
                      <span className="text-gray-600">Direct and concise responses</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" disabled checked className="rounded" />
                      <span className="text-gray-600">Include source citations</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" disabled className="rounded" />
                      <span className="text-gray-600">Verbose explanations</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Coming soon</p>
                </div>

                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium mb-3">Memory Management</h4>
                  <p className="text-xs text-gray-500 mb-2">Things the AI remembers about you across conversations.</p>
                  <button className="px-3 py-1.5 border rounded text-sm text-gray-600" disabled>
                    View Stored Memories
                  </button>
                  <p className="text-xs text-gray-400 mt-2">Coming soon — view and delete specific memories</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div>
              <h2 className="text-lg font-semibold text-navy mb-2">System Settings</h2>
              <p className="text-sm text-gray-500 mb-6">Platform configuration and monitoring.</p>

              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium mb-3">AI Model</h4>
                  <p className="text-sm text-gray-600">Currently using: <strong>Claude Haiku 4.5</strong></p>
                  <p className="text-xs text-gray-400 mt-1">Model selection coming soon</p>
                </div>

                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium mb-3">Cost & Usage</h4>
                  <p className="text-xs text-gray-500 mb-2">Monthly spending and usage metrics.</p>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <p className="text-lg font-semibold text-navy">—</p>
                      <p className="text-xs text-gray-500">This month</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <p className="text-lg font-semibold text-navy">—</p>
                      <p className="text-xs text-gray-500">Queries today</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <p className="text-lg font-semibold text-navy">$500</p>
                      <p className="text-xs text-gray-500">Budget limit</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Real-time cost tracking coming soon</p>
                </div>

                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium mb-3">Escalation Settings</h4>
                  <p className="text-xs text-gray-500 mb-2">Configure when and how you get notified.</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" disabled checked className="rounded" />
                      <span className="text-gray-600">Email on Tier 2 escalations</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" disabled checked className="rounded" />
                      <span className="text-gray-600">Email on Tier 3 escalations</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" disabled className="rounded" />
                      <span className="text-gray-600">SMS notifications</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Coming soon</p>
                </div>

                <div className="bg-white rounded-lg border p-4">
                  <h4 className="font-medium mb-3">Knowledge Base Status</h4>
                  <p className="text-xs text-gray-500 mb-2">Document sync status for each module.</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between py-1"><span>Warranty</span><span className="text-yellow-600">Pending setup</span></div>
                    <div className="flex justify-between py-1"><span>Project Management</span><span className="text-yellow-600">Pending setup</span></div>
                    <div className="flex justify-between py-1"><span>CAD Development</span><span className="text-yellow-600">Pending setup</span></div>
                    <div className="flex justify-between py-1"><span>Sales & Pricing</span><span className="text-yellow-600">Pending setup</span></div>
                    <div className="flex justify-between py-1"><span>Owner Strategic</span><span className="text-yellow-600">Pending setup</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'roadmap' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-navy">Roadmap</h2>
                  <p className="text-sm text-gray-500">Track ideas, planned features, and user feedback.</p>
                </div>
                <button
                  onClick={() => setShowAddRoadmap(!showAddRoadmap)}
                  className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-medium hover:bg-opacity-90"
                >
                  + Add Idea
                </button>
              </div>

              {showAddRoadmap && (
                <div className="mb-6 p-4 bg-white rounded-lg border">
                  <h4 className="font-medium mb-3">Add New Item</h4>
                  <form onSubmit={handleAddRoadmapItem} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={newRoadmapItem.title}
                        onChange={(e) => setNewRoadmapItem({...newRoadmapItem, title: e.target.value})}
                        className="w-full px-3 py-2 border rounded text-sm"
                        placeholder="What's the idea or feature?"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
                      <textarea
                        value={newRoadmapItem.description}
                        onChange={(e) => setNewRoadmapItem({...newRoadmapItem, description: e.target.value})}
                        className="w-full px-3 py-2 border rounded text-sm"
                        rows={2}
                        placeholder="Details, context, or notes"
                      />
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                        <select
                          value={newRoadmapItem.category}
                          onChange={(e) => setNewRoadmapItem({...newRoadmapItem, category: e.target.value})}
                          className="w-full px-3 py-2 border rounded text-sm"
                        >
                          <option value="feature">Feature</option>
                          <option value="integration">Integration</option>
                          <option value="improvement">Improvement</option>
                          <option value="fix">Fix</option>
                          <option value="someday">Someday / Maybe</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                        <select
                          value={newRoadmapItem.priority}
                          onChange={(e) => setNewRoadmapItem({...newRoadmapItem, priority: e.target.value})}
                          className="w-full px-3 py-2 border rounded text-sm"
                        >
                          <option value="high">🔴 High</option>
                          <option value="medium">🟡 Medium</option>
                          <option value="low">🟢 Low</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-2 bg-blue text-white rounded text-sm font-medium">Save</button>
                      <button type="button" onClick={() => setShowAddRoadmap(false)} className="px-4 py-2 border rounded text-sm">Cancel</button>
                    </div>
                  </form>
                </div>
              )}

              {/* Filters */}
              <div className="mb-4 p-3 bg-white rounded-lg border flex flex-wrap items-center gap-4">
                <span className="text-xs font-medium text-gray-500">Show:</span>
                {[
                  { id: 'new', label: '🆕 New' },
                  { id: 'idea', label: '💡 Ideas' },
                  { id: 'planned', label: '📋 Planned' },
                  { id: 'in-progress', label: '🚧 In Progress' },
                  { id: 'submitted', label: '📩 Feedback' },
                  { id: 'done', label: '✅ Done' },
                  { id: 'ignored', label: '🚫 Ignored' },
                ].map(s => (
                  <label key={s.id} className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleStatuses.has(s.id)}
                      onChange={() => toggleStatus(s.id)}
                      className="rounded border-gray-300"
                    />
                    <span className={visibleStatuses.has(s.id) ? 'text-gray-700' : 'text-gray-400'}>{s.label}</span>
                  </label>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sortByDate}
                      onChange={() => setSortByDate(!sortByDate)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-gray-600">Sort all by date</span>
                  </label>
                </div>
              </div>

              {roadmapLoading ? (
                <div className="text-gray-500">Loading...</div>
              ) : (
                <div className="space-y-6">
                  {sortByDate ? (
                    <div className="bg-white rounded-lg border">
                      <div className="p-4 border-b"><h4 className="font-medium">📅 All Items (newest first)</h4></div>
                      <ul className="divide-y">
                        {roadmapItems
                          .filter(i => visibleStatuses.has(i.status))
                          .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
                          .map((item: any) => (
                            <li key={item.itemId} className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{item.title}</p>
                                  {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                                  {item.context && <p className="text-xs text-gray-400 mt-1 italic whitespace-pre-line">{item.context.substring(0, 200)}</p>}
                                  <p className="text-xs text-gray-400 mt-1">
                                    {item.type === 'feedback' && <span className="font-medium text-purple-600">From: {item.userName || 'Unknown'} • </span>}
                                    {item.category} • {new Date(item.createdAt).toLocaleDateString()} {new Date(item.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </p>
                                  {item.adminNotes && (
                                    <p className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded">💬 Admin note: {item.adminNotes}</p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1 ml-4">
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={item.status}
                                      onChange={(e) => handleUpdateStatus(item.itemId, e.target.value)}
                                      className="text-xs border rounded px-2 py-1"
                                    >
                                      <option value="new">New</option>
                                      <option value="idea">Idea</option>
                                      <option value="planned">Planned</option>
                                      <option value="in-progress">In Progress</option>
                                      <option value="submitted">Submitted</option>
                                      <option value="done">Done</option>
                                      <option value="ignored">Ignored</option>
                                    </select>
                                    <button onClick={() => handleDeleteRoadmapItem(item.itemId)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                                  </div>
                                  <button
                                    onClick={() => {
                                      const note = prompt('Add a note (visible to the user who submitted this):', item.adminNotes || '');
                                      if (note !== null) handleAddNote(item.itemId, note);
                                    }}
                                    className="text-xs text-gray-400 hover:text-blue-500"
                                  >
                                    {item.adminNotes ? '✏️ Edit note' : '💬 Add note'}
                                  </button>
                                </div>
                              </div>
                            </li>
                          ))}
                      </ul>
                      {roadmapItems.filter(i => visibleStatuses.has(i.status)).length === 0 && (
                        <div className="p-4 text-gray-400 text-center text-sm">No items match the selected filters.</div>
                      )}
                    </div>
                  ) : (
                    <>
                    {['new', 'idea', 'planned', 'in-progress', 'submitted', 'done', 'ignored'].filter(s => visibleStatuses.has(s)).map(status => {
                    const items = roadmapItems.filter(i => i.status === status);
                    if (items.length === 0) return null;
                    const labels: Record<string, string> = { new: '🆕 New', idea: '💡 Ideas', planned: '📋 Planned', 'in-progress': '🚧 In Progress', submitted: '📩 Submitted Feedback', done: '✅ Done', ignored: '🚫 Ignored' };
                    return (
                      <div key={status} className="bg-white rounded-lg border">
                        <div className="p-4 border-b"><h4 className="font-medium">{labels[status] || status}</h4></div>
                        <ul className="divide-y">
                          {items.map((item: any) => (
                            <li key={item.itemId} className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{item.title}</p>
                                  {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                                  {item.context && <p className="text-xs text-gray-400 mt-1 italic whitespace-pre-line">{item.context.substring(0, 200)}</p>}
                                  <p className="text-xs text-gray-400 mt-1">
                                    {item.type === 'feedback' && <span className="font-medium text-purple-600">From: {item.userName || 'Unknown'} • </span>}
                                    {item.category} • {new Date(item.createdAt).toLocaleDateString()} {new Date(item.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </p>
                                  {item.adminNotes && (
                                    <p className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded">💬 Admin note: {item.adminNotes}</p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1 ml-4">
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={item.status}
                                      onChange={(e) => handleUpdateStatus(item.itemId, e.target.value)}
                                      className="text-xs border rounded px-2 py-1"
                                    >
                                      <option value="new">New</option>
                                      <option value="idea">Idea</option>
                                      <option value="planned">Planned</option>
                                      <option value="in-progress">In Progress</option>
                                      <option value="submitted">Submitted</option>
                                      <option value="done">Done</option>
                                      <option value="ignored">Ignored</option>
                                    </select>
                                    <button onClick={() => handleDeleteRoadmapItem(item.itemId)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                                  </div>
                                  <button
                                    onClick={() => {
                                      const note = prompt('Add a note (visible to the user who submitted this):', item.adminNotes || '');
                                      if (note !== null) handleAddNote(item.itemId, note);
                                    }}
                                    className="text-xs text-gray-400 hover:text-blue-500"
                                  >
                                    {item.adminNotes ? '✏️ Edit note' : '💬 Add note'}
                                  </button>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                    </>
                  )}
                  {roadmapItems.length === 0 && (
                    <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                      <p>No roadmap items yet.</p>
                      <p className="text-xs mt-1">Items created here or flagged from chat will appear here.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'feedback' && (
            <div>
              <h2 className="text-lg font-semibold text-navy mb-2">Feedback</h2>
              <p className="text-sm text-gray-500 mb-6">Submit suggestions, report issues, or request features.</p>

              <div className="bg-white rounded-lg border p-4 mb-6">
                <h4 className="font-medium mb-3">Submit Feedback</h4>
                <form onSubmit={handleSubmitFeedback} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                    <input
                      type="text"
                      value={feedbackForm.title}
                      onChange={(e) => setFeedbackForm({...feedbackForm, title: e.target.value})}
                      className="w-full px-3 py-2 border rounded text-sm"
                      placeholder="Brief summary of your feedback"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                    <select
                      value={feedbackForm.category}
                      onChange={(e) => setFeedbackForm({...feedbackForm, category: e.target.value})}
                      className="w-full px-3 py-2 border rounded text-sm"
                    >
                      <option value="general">General</option>
                      <option value="bug">Bug Report</option>
                      <option value="feature">Feature Request</option>
                      <option value="improvement">Improvement</option>
                      <option value="question">Question</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Details (optional)</label>
                    <textarea
                      value={feedbackForm.description}
                      onChange={(e) => setFeedbackForm({...feedbackForm, description: e.target.value})}
                      className="w-full px-3 py-2 border rounded text-sm"
                      rows={3}
                      placeholder="Provide additional context or details"
                    />
                  </div>
                  <button type="submit" className="px-4 py-2 bg-blue text-white rounded text-sm font-medium hover:bg-opacity-90">
                    Submit Feedback
                  </button>
                </form>
              </div>

              {/* User's submitted feedback */}
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b"><h4 className="font-medium">Your Submissions</h4></div>
                {roadmapItems.filter(i => i.type === 'feedback' && i.userId === user?.userId).length === 0 ? (
                  <div className="p-4 text-gray-500 text-sm">No feedback submitted yet.</div>
                ) : (
                  <ul className="divide-y">
                    {roadmapItems.filter(i => i.type === 'feedback' && i.userId === user?.userId).map((item: any) => (
                      <li key={item.itemId} className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium">{item.title}</p>
                            {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                            <p className="text-xs text-gray-400 mt-1">{item.category} • {new Date(item.createdAt).toLocaleDateString()}</p>
                            {item.adminNotes && (
                              <p className="text-xs text-blue-600 mt-2 bg-blue-50 px-2 py-1 rounded">💬 Response: {item.adminNotes}</p>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.status === 'done' ? 'bg-green-100 text-green-700' :
                            item.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {item.status}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
